import { supabase } from './supabase';
import { getActiveEventId } from './event';
import type { User } from '../types';

export interface TestPermission {
  can_view: boolean;
  can_enter: boolean;
  can_edit: boolean;
  can_delete: boolean;
}

export interface TestInfo {
  id: string;
  testCode: string;
  testName: string;
  category: string;
  enabled: boolean;
  required: boolean;
  displayOrder: number;
}

/** Tests that are permanently accessible to broad roles regardless of per-test permission rows. */
export const BROAD_TEST_ROLES: User['role'][] = ['master_admin', 'admin', 'all_vitals'];

/** Fallback single-test mapping for legacy operator roles (used when no permission row exists). */
export const OPERATOR_TEST_MAP: Record<string, string> = {
  bp: 'bp',
  heart_rate: 'hr',
  spo2: 'spo2',
  temperature: 'temp',
  rbs: 'rbs',
  cholesterol: 'chol',
  height_weight: 'height_weight',
  blood_grouping: 'blood_grouping',
  colour_blindness: 'colour_blindness',
  visual_acuity: 'visual_acuity',
  body_fat: 'body_fat',
  chest_circumference: 'chest_circ',
  respiratory: 'respiratory',
};

const isBroadRole = (role: User['role']): boolean => BROAD_TEST_ROLES.includes(role);

/**
 * Retry a Supabase read against transient network failures ("Failed to fetch").
 * These blips are common in embedded/preview environments; a short retry with
 * backoff makes the sidebar & test entry pages resilient instead of erroring out.
 */
const withRetry = async <T,>(operation: () => Promise<T>, fallback: T, retries = 3): Promise<T> => {
  let lastError: unknown = null;
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      return await operation();
    } catch (err) {
      lastError = err;
      const msg = (err as { message?: string })?.message || '';
      const isNetworkError = /Failed to fetch|NetworkError|fetch failed|load failed/i.test(msg);
      if (!isNetworkError) break;
      if (attempt < retries) {
        await new Promise((res) => setTimeout(res, 600 * attempt));
      }
    }
  }
  // Only log once and quietly so transient blips don't spam/error-map.
  console.warn('Supabase read failed, using fallback:', (lastError as { message?: string })?.message);
  return fallback;
};

/** Fetch the set of tests enabled for a given event (defaults to active event). */
export const getEventEnabledTestIds = async (eventId: string = getActiveEventId()): Promise<string[]> => {
  return withRetry(
    async () => {
      const { data, error } = await supabase
        .from('event_tests')
        .select('test_id')
        .eq('event_id', eventId)
        .eq('enabled', true);
      if (error) throw error;
      return (data || []).map((r: Record<string, unknown>) => r.test_id as string);
    },
    [] as string[]
  );
};

/** Fetch the full test master (all tests, regardless of event). */
export const getTestMaster = async (): Promise<TestInfo[]> => {
  const { data, error } = await supabase
    .from('tests')
    .select('id, test_code, test_name, category, status, display_order')
    .order('display_order', { ascending: true });
  if (error) {
    console.error('Error fetching test master:', error);
    return [];
  }
  return (data || []).map((t: Record<string, unknown>) => ({
    id: t.id as string,
    testCode: t.test_code as string,
    testName: t.test_name as string,
    category: t.category as string,
    enabled: true,
    required: false,
    displayOrder: (t.display_order as number) || 0,
  }));
};

/** Fetch event test config rows for an event. */
export const getEventTests = async (eventId: string): Promise<TestInfo[]> => {
  const master = await getTestMaster();
  const { data, error } = await supabase
    .from('event_tests')
    .select('test_id, enabled, required, display_order')
    .eq('event_id', eventId);
  if (error) {
    console.error('Error fetching event tests:', error);
    return master.map((t) => ({ ...t, enabled: false }));
  }
  const config = (data || []).reduce<Record<string, { enabled: boolean; required: boolean; display_order: number }>>(
    (acc, r) => {
      acc[(r as Record<string, unknown>).test_id as string] = {
        enabled: (r as Record<string, unknown>).enabled as boolean,
        required: (r as Record<string, unknown>).required as boolean,
        display_order: (r as Record<string, unknown>).display_order as number,
      };
      return acc;
    },
    {}
  );
  return master
    .map((t) => {
      const c = config[t.id];
      return { ...t, enabled: c?.enabled ?? false, required: c?.required ?? false, displayOrder: c?.display_order ?? t.displayOrder };
    })
    .sort((a, b) => a.displayOrder - b.displayOrder);
};

/** Fetch a role's test permissions (map test_id -> permission). */
export const getRoleTestPermissions = async (role: string): Promise<Record<string, TestPermission>> => {
  return withRetry(
    async () => {
      const { data, error } = await supabase
        .from('role_test_permissions')
        .select('test_id, can_view, can_enter, can_edit, can_delete')
        .eq('role_id', role);
      if (error) throw error;
      return (data || []).reduce<Record<string, TestPermission>>((acc, r) => {
        acc[(r as Record<string, unknown>).test_id as string] = {
          can_view: (r as Record<string, unknown>).can_view as boolean,
          can_enter: (r as Record<string, unknown>).can_enter as boolean,
          can_edit: (r as Record<string, unknown>).can_edit as boolean,
          can_delete: (r as Record<string, unknown>).can_delete as boolean,
        };
        return acc;
      }, {});
    },
    {} as Record<string, TestPermission>
  );
};

/**
 * ACCESS RULE:
 * A user gets test access ONLY if:
 *   1. The test is enabled for the active event
 *   AND
 *   2. Their role has permission for that test (broad roles all pass)
 */
export const canAccessTest = async (user: User | null, testId: string): Promise<boolean> => {
  if (!user) return false;

  // 1. Test must be enabled for the active event
  const enabled = await getEventEnabledTestIds();
  if (!enabled.includes(testId)) return false;

  // 2. Role permission
  if (isBroadRole(user.role)) return true;

  const perms = await getRoleTestPermissions(user.role);
  const perm = perms[testId];
  if (perm && (perm.can_view || perm.can_enter)) return true;

  // Legacy operator fallback: role -> single test mapping
  return OPERATOR_TEST_MAP[user.role] === testId;
};

/** Add or update a test in the global test master. */
export const saveTest = async (test: TestInfo): Promise<void> => {
  const { error } = await supabase
    .from('tests')
    .upsert([{
      id: test.id,
      test_code: test.testCode,
      test_name: test.testName,
      category: test.category ?? null,
      status: 'active',
      display_order: test.displayOrder,
      updated_at: new Date().toISOString(),
    }]);
  if (error) {
    console.error('Error saving test:', error);
    throw error;
  }
};

/** Save the per-event test configuration (enabled/required/display_order). */
export const saveEventTestConfig = async (
  eventId: string,
  config: { testId: string; enabled: boolean; required: boolean; displayOrder: number }[]
): Promise<void> => {
  const rows = config.map((c) => ({
    id: `${eventId}-${c.testId}`,
    event_id: eventId,
    test_id: c.testId,
    enabled: c.enabled,
    required: c.required,
    display_order: c.displayOrder,
  }));
  const { error } = await supabase
    .from('event_tests')
    .upsert(rows, { onConflict: 'id' });
  if (error) {
    console.error('Error saving event test config:', error);
    throw error;
  }
};

/** Return the list of test ids the user may access for the active event (for sidebar/dashboards). */
export const getAccessibleTestIds = async (user: User | null): Promise<string[]> => {
  if (!user) return [];
  const enabled = await getEventEnabledTestIds();
  if (isBroadRole(user.role)) return enabled;

  const perms = await getRoleTestPermissions(user.role);
  return enabled.filter((testId) => {
    const perm = perms[testId];
    if (perm && (perm.can_view || perm.can_enter)) return true;
    return OPERATOR_TEST_MAP[user.role] === testId;
  });
};